<template>
  <div class="min-h-screen bg-gray-950 p-6 bg-grid-pattern">
    <!-- Background effects -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-red/10 rounded-full blur-3xl"
      ></div>
    </div>

    <div class="relative max-w-7xl mx-auto">
      <!-- Header -->
      <div class="card">
        <div
          class="px-6 py-4 border-b border-gray-800 flex justify-between items-center"
        >
          <h1 class="text-xl font-semibold text-white font-display">
            Управление приложениями
          </h1>
          <button
            @click="showCreateDialog = true"
            class="btn-primary inline-flex items-center px-4 py-2 text-sm font-medium"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              ></path>
            </svg>
            Создать приложение
          </button>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-800">
            <thead class="bg-gray-900/50">
              <tr>
                <th class="table-header">Название</th>
                <th class="table-header">Key</th>
                <th class="table-header">Secret</th>
                <th class="table-header">Создано</th>
                <th class="table-header">Действия</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-800">
              <tr v-if="loading">
                <td colspan="5" class="px-6 py-12 text-center">
                  <div class="flex justify-center">
                    <svg
                      class="animate-spin h-8 w-8 text-primary-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                </td>
              </tr>
              <tr v-else-if="applications.length === 0">
                <td colspan="5" class="px-6 py-12 text-center text-gray-400">
                  <div class="flex flex-col items-center">
                    <svg class="w-12 h-12 text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path>
                    </svg>
                    <p>Нет приложений</p>
                    <button @click="showCreateDialog = true" class="mt-4 text-primary-500 hover:text-primary-400 font-medium">
                      Создать первое приложение
                    </button>
                  </div>
                </td>
              </tr>
              <tr
                v-else
                v-for="app in applications"
                :key="app.id"
                class="table-row"
              >
                <td class="table-cell font-medium text-white">
                  <div class="flex items-center">
                    {{ app.name }}
                    <span
                      v-if="(app.allowed_origins?.length > 0) || (app.allowed_ips?.length > 0)"
                      class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-500/20 text-primary-400"
                      title="Включены ограничения доступа"
                    >
                      <svg class="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </span>
                  </div>
                </td>
                <td class="table-cell text-gray-400 font-mono text-xs">
                  <div class="flex items-center gap-1">
                    <span class="bg-gray-800 px-2 py-1 rounded">{{ app.key }}</span>
                    <button
                      type="button"
                      @click="copyKey(app)"
                      class="p-1 text-gray-500 hover:text-primary-400 transition-colors"
                      title="Скопировать key"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                      </svg>
                    </button>
                  </div>
                </td>
                <td class="table-cell text-gray-400 font-mono text-xs">
                  <div class="flex items-center gap-1">
                    <span
                      v-if="revealedSecrets[app.id]"
                      class="block max-w-xs break-all bg-gray-800 px-2 py-1 rounded"
                    >{{ revealedSecrets[app.id] }}</span>
                    <span v-else class="bg-gray-800 px-2 py-1 rounded tracking-widest select-none">••••••••••••••••</span>
                    <button
                      type="button"
                      @click="toggleSecret(app)"
                      :disabled="secretLoading[app.id]"
                      class="p-1 text-gray-500 hover:text-primary-400 transition-colors disabled:opacity-50"
                      :title="revealedSecrets[app.id] ? 'Скрыть secret' : 'Показать secret'"
                    >
                      <svg v-if="!revealedSecrets[app.id]" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="copySecret(app)"
                      class="p-1 text-gray-500 hover:text-primary-400 transition-colors"
                      title="Скопировать secret"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                      </svg>
                    </button>
                  </div>
                </td>
                <td class="table-cell text-gray-400">
                  {{ formatDate(app.created_at) }}
                </td>
                <td class="table-cell">
                  <div class="flex space-x-2">
                    <button
                      @click="editApplication(app)"
                      class="text-primary-500 hover:text-primary-400 inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-primary-500/10 hover:bg-primary-500/20 transition-colors"
                    >
                      <svg
                        class="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        ></path>
                      </svg>
                      Редактировать
                    </button>
                    <button
                      @click="deleteApplication(app)"
                      class="text-red-400 hover:text-red-300 inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-red-500/10 hover:bg-red-500/20 transition-colors"
                    >
                      <svg
                        class="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        ></path>
                      </svg>
                      Удалить
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Modal -->
      <Transition name="modal">
        <div
          v-if="showCreateDialog"
          class="fixed inset-0 bg-gray-950/80 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center"
          @click.self="showCreateDialog = false"
        >
          <div
            class="relative mx-auto p-6 w-full max-w-lg card max-h-[90vh] overflow-y-auto"
          >
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-xl font-semibold text-white font-display">
                {{ editingApp ? 'Редактировать приложение' : 'Создать приложение' }}
              </h3>
              <button
                @click="showCreateDialog = false"
                class="text-gray-500 hover:text-gray-400 transition-colors"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>

            <form @submit.prevent="saveApplication" class="space-y-5">
              <div>
                <label class="form-label">Название</label>
                <input
                  v-model="form.name"
                  type="text"
                  required
                  class="input-field"
                  placeholder="Введите название приложения"
                />
                <div v-if="errors.name" class="mt-1 text-sm text-red-400">
                  {{ errors.name }}
                </div>
              </div>

              <!-- Ключи и ограничения доступа (только при редактировании) -->
              <template v-if="editingApp">
                <div class="pt-4 border-t border-gray-800">
                  <h4 class="text-sm font-medium text-white mb-1">Ключи доступа</h4>
                  <p class="text-xs text-gray-500 mb-3">
                    Если secret мог попасть к посторонним, перевыпустите ключи. Старые key и secret перестанут работать сразу — обновите их на своих серверах и в клиентах.
                  </p>
                  <button
                    type="button"
                    @click="regenerateCredentials"
                    :disabled="regenerating"
                    class="text-sm text-red-400 hover:text-red-300 inline-flex items-center px-3 py-1.5 font-medium rounded-lg bg-red-500/10 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                  >
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {{ regenerating ? 'Перевыпускаем…' : 'Перевыпустить ключи' }}
                  </button>
                </div>

                <div class="pt-4 border-t border-gray-800">
                  <h4 class="text-sm font-medium text-white mb-3 flex items-center">
                    <svg class="w-4 h-4 mr-2 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Ограничения доступа
                  </h4>
                  <p class="text-xs text-gray-500 mb-4">
                    Оставьте пустым, чтобы разрешить подключения отовсюду
                  </p>
                  
                  <!-- Разрешённые домены -->
                  <div class="mb-4">
                    <label class="form-label text-sm">Разрешённые домены</label>
                    <div class="space-y-2">
                      <div
                        v-for="(origin, index) in form.allowed_origins"
                        :key="'origin-' + index"
                        class="flex items-center space-x-2"
                      >
                        <input
                          v-model="form.allowed_origins[index]"
                          type="text"
                          class="input-field flex-1 text-sm"
                          placeholder="*.example.com или example.com"
                        />
                        <button
                          type="button"
                          @click="removeOrigin(index)"
                          class="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <button
                        type="button"
                        @click="addOrigin"
                        class="text-sm text-primary-500 hover:text-primary-400 flex items-center"
                      >
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Добавить домен
                      </button>
                    </div>
                    <div v-if="errors.allowed_origins" class="mt-1 text-sm text-red-400">
                      {{ errors.allowed_origins }}
                    </div>
                  </div>

                  <!-- Разрешённые IP -->
                  <div>
                    <label class="form-label text-sm">Разрешённые IP-адреса</label>
                    <div class="space-y-2">
                      <div
                        v-for="(ip, index) in form.allowed_ips"
                        :key="'ip-' + index"
                        class="flex items-center space-x-2"
                      >
                        <input
                          v-model="form.allowed_ips[index]"
                          type="text"
                          class="input-field flex-1 text-sm"
                          placeholder="192.168.1.1 или 192.168.1.0/24"
                        />
                        <button
                          type="button"
                          @click="removeIP(index)"
                          class="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <button
                        type="button"
                        @click="addIP"
                        class="text-sm text-primary-500 hover:text-primary-400 flex items-center"
                      >
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Добавить IP
                      </button>
                    </div>
                    <div v-if="errors.allowed_ips" class="mt-1 text-sm text-red-400">
                      {{ errors.allowed_ips }}
                    </div>
                  </div>

                  <!-- Подсказка о текущих ограничениях -->
                  <div v-if="hasRestrictions" class="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <p class="text-xs text-amber-400 flex items-start">
                      <svg class="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Включены ограничения. Подключения будут разрешены только с указанных доменов/IP.
                    </p>
                  </div>
                </div>
              </template>

              <div class="flex justify-end space-x-3 pt-4">
                <button type="button" @click="showCreateDialog = false" class="btn-secondary">
                  Отмена
                </button>
                <button
                  type="submit"
                  :disabled="saving"
                  class="btn-primary flex items-center"
                >
                  <svg
                    v-if="saving"
                    class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {{ editingApp ? 'Сохранить' : 'Создать' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script>
import { useApplicationsStore } from '../stores/applications';
import { useNotifications } from '../composables/useNotifications';
import { copyText } from '../services/clipboard';

export default {
  name: 'Applications',
  setup() {
    const applicationsStore = useApplicationsStore();
    const { success, error } = useNotifications();

    return {
      applicationsStore,
      success,
      error,
    };
  },
  data() {
    return {
      showCreateDialog: false,
      editingApp: null,
      saving: false,
      regenerating: false,
      // Секреты, раскрытые кнопкой «глаз»: id приложения → secret
      revealedSecrets: {},
      secretLoading: {},
      form: {
        name: '',
        allowed_origins: [],
        allowed_ips: [],
      },
      errors: {},
    };
  },
  computed: {
    applications() {
      return this.applicationsStore.applicationsList;
    },
    loading() {
      return this.applicationsStore.isLoading;
    },
    hasRestrictions() {
      return (this.form.allowed_origins?.length > 0 && this.form.allowed_origins.some(o => o.trim())) 
          || (this.form.allowed_ips?.length > 0 && this.form.allowed_ips.some(ip => ip.trim()));
    },
  },
  mounted() {
    this.fetchApplications();
  },
  methods: {
    async fetchApplications() {
      await this.applicationsStore.fetchApplications();
    },

    editApplication(app) {
      this.editingApp = app;
      this.form = {
        name: app.name,
        allowed_origins: [...(app.allowed_origins || [])],
        allowed_ips: [...(app.allowed_ips || [])],
      };
      this.errors = {};
      this.showCreateDialog = true;
    },

    addOrigin() {
      this.form.allowed_origins.push('');
    },

    removeOrigin(index) {
      this.form.allowed_origins.splice(index, 1);
    },

    addIP() {
      this.form.allowed_ips.push('');
    },

    removeIP(index) {
      this.form.allowed_ips.splice(index, 1);
    },

    validateForm() {
      this.errors = {};

      if (!this.form.name) {
        this.errors.name = 'Введите название приложения';
        return false;
      }

      if (this.form.name.length < 2) {
        this.errors.name = 'Название должно содержать минимум 2 символа';
        return false;
      }

      return true;
    },

    async saveApplication() {
      if (!this.validateForm()) {
        return;
      }

      try {
        this.saving = true;

        // Фильтруем пустые значения из массивов
        const formData = {
          name: this.form.name,
          allowed_origins: this.form.allowed_origins.filter(o => o && o.trim()),
          allowed_ips: this.form.allowed_ips.filter(ip => ip && ip.trim()),
        };

        if (this.editingApp) {
          await this.applicationsStore.updateApplicationById({
            id: this.editingApp.id,
            ...formData,
          });
          this.success('Успех', 'Приложение обновлено');
        } else {
          await this.applicationsStore.createApplication({ name: formData.name });
          this.success('Успех', 'Приложение создано');
        }

        this.showCreateDialog = false;
        this.editingApp = null;
        this.form = { name: '', allowed_origins: [], allowed_ips: [] };
        this.errors = {};
      } catch (error) {
        this.error('Ошибка', 'Ошибка при сохранении приложения');
      } finally {
        this.saving = false;
      }
    },

    async deleteApplication(app) {
      if (confirm('Вы уверены, что хотите удалить это приложение?')) {
        try {
          await this.applicationsStore.deleteApplication(app.id);
          delete this.revealedSecrets[app.id];
          this.success('Успех', 'Приложение удалено');
        } catch (error) {
          this.error('Ошибка', 'Ошибка при удалении приложения');
        }
      }
    },

    async toggleSecret(app) {
      if (this.revealedSecrets[app.id]) {
        delete this.revealedSecrets[app.id];
        return;
      }

      try {
        this.secretLoading[app.id] = true;
        this.revealedSecrets[app.id] = await this.applicationsStore.fetchSecret(app.id);
      } catch (error) {
        this.error('Ошибка', 'Не удалось получить secret');
      } finally {
        delete this.secretLoading[app.id];
      }
    },

    async copyKey(app) {
      try {
        await copyText(app.key);
        this.success('Скопировано', 'Key в буфере обмена');
      } catch (error) {
        this.error('Ошибка', 'Не удалось скопировать key');
      }
    },

    async copySecret(app) {
      try {
        // Промис передаём сразу, без await: иначе Safari потеряет жест пользователя
        await copyText(this.applicationsStore.fetchSecret(app.id));
        this.success('Скопировано', 'Secret в буфере обмена');
      } catch (error) {
        this.error('Ошибка', 'Не удалось скопировать secret');
      }
    },

    async regenerateCredentials() {
      const app = this.editingApp;
      const question =
        `Перевыпустить ключи приложения «${app.name}»?\n\n` +
        'Старые key и secret перестанут работать сразу. Это нельзя отменить.';
      if (!confirm(question)) {
        return;
      }

      try {
        this.regenerating = true;
        const { key } = await this.applicationsStore.regenerateCredentials(app.id);
        this.editingApp = { ...app, key };
        delete this.revealedSecrets[app.id];
        this.success('Ключи перевыпущены', 'Обновите key и secret в своих интеграциях');
      } catch (error) {
        this.error('Ошибка', 'Не удалось перевыпустить ключи');
      } finally {
        this.regenerating = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
